/**
 * The primary class for the plugin.
 */

import internalApi from './internalApi';

class Quickview {
	constructor() {
		this.api = new internalApi();
		this.body = document.querySelector( 'body' );
		// Classes we target in different contexts.
		this.target = 'quickview-product__button';
		this.pdpTarget = 'single_add_to_cart_button';
		this.modalClass = 'qala-quickview';
		this.loaderClass = 'qala-quickview-loader__overlay';
		this.loadingClass = 'quickview-product__button--loading';
		this.errorClass = 'quickview-product__button--error';
		this.notifyClass = 'quickview-product__button--notify';
		// This is a very generic class which WC uses too. Which is why we dont set it similar to the other classes.
		this.disabledClass = 'disabled';
		// In first iteration we only support handling simple and variable products.
		const supportedProductTypes = QalaBuy?.allowedProductTypes ?? [ 'simple', 'variable' ];

		/**
		 * Filters the supported product types.
		 *
		 * @since 1.0
		 *
		 * @param {string} filterName - The name of the filter being applied.
		 * @param {Array}  data       - the array of allowed product types.
		 *
		 * @return {Array} - The filtered array
		 */
		this.supportedProductTypes = wp.hooks.applyFilters( 'qalaBuy.filter.supportedProductTypes', supportedProductTypes );

		// Document ready.
		document.defaultView.addEventListener( 'load', this.getElements );
		document.defaultView.addEventListener( 'load', this.events );
	}

	/**
	 * Get some elements we will be using.
	 */
	getElements = () => {
		this.modal = document.getElementById( this.modalClass );
		this.wrapper = this.modal?.querySelector( '.' + this.modalClass + '__wrapper' );
		this.overlay = this.modal?.querySelector( '.' + this.modalClass + '__overlay' );
		this.modalLoader = this.modal?.querySelector( '.' + this.loaderClass );
		this.modalCloseButton = this.modal?.querySelector( '.header__close-btn' );
	};

	/**
	 * Bind some events.
	 */
	events = () => {
		// Since button is replaced by AJAX we add the event listener to the body
		this.body.addEventListener( 'click', event => {
			// Check if the clicked element is our quickview button.
			if ( event.target.classList.contains( this.target ) ) {
				this.triggerQuickview( event, event.target );
				return;
			}
			// Let's also check the parent elements since we might've triggered the click through a child element on the button.
			const parentButton = event.target.closest( '.' + this.target );
			if ( parentButton ) {
				this.triggerQuickview( event, parentButton );
				return;
			}

			// Finally lets check if the clicked element is the PDP button.
			// But only if the setting is enabled.
			if ( QalaBuy?.settings?.enable_single_product === 'yes' ) {
				if ( event.target.classList.contains( this.pdpTarget ) && ! event.target.closest( '#qala-quickview' ) ) {
					this.addToCartTriggered( event, 'single-product' );
				}
			}
		} );

		// Add event listener to see if we click the overlay.
		this.body.ownerDocument.addEventListener( 'click', this.outsideClickListener );

		// Add event listener to see if we click the close button in the modal.
		this.modalCloseButton?.addEventListener( 'click', this.modalCloseClickListener );

		// add event listener to see if the cart form elements change.
		const cartForm = this.body.querySelector( 'form.cart' );
		if ( cartForm ) {
			cartForm.addEventListener( 'focusin', this.cartFormChangeListener );
		}
	};

	/**
	 * Reacts to changes in a select element in our form.
	 *
	 * this means we need to update the selected variation.
	 * Its presently done using the WC core script so all we need to do here is update our button state.
	 *
	 * @param {Event} event
	 */
	changeVariationSelection = event => {
		const selectElement = event.target;
		const value = selectElement.value;

		if ( value === '' ) {
			// The default option is selected.
			this.setButtonDisabledState();
			return;
		}

		if ( selectElement.options[ selectElement.selectedIndex ]?.dataset?.instock === '1' ) {
			this.setButtonEnabledState();
		} else {
			this.setButtonDisabledState();
		}

		// Here's where WC does the variation selection.
		// site/public/wp-content/plugins/woocommerce/assets/js/frontend/add-to-cart-variation.js
		// VariationForm.prototype.onFindVariation
	};

	/**
	 * Handles when we want to add to cart on a trigger click.
	 *
	 * This deals with the add to cart button inside the quickview and sends the necessary data to the form preparation.
	 * It also handles if the trigger happened from a single product page.
	 *
	 * @param {Event}  event
	 * @param {string} context
	 */
	addToCartTriggered = ( event, context ) => {
		const buttonElement = event.target;
		let formElement;
		let productType = 'variable';
		if ( context === 'single-product' ) {
			// in the case of a single product page the button is inside the form.
			formElement = buttonElement.closest( 'form' );
		} else {
			context = 'quickview';
			// However in the quickview the button is separate from the form.
			productType = this.modal.querySelector( '.product' )?.dataset.type ?? 'variable';
			formElement = ( productType === 'variable' ) ? this.modal.querySelector( '.variations_form' ) : null;
		}
		this.prepareSubmitData( event, buttonElement, formElement, context, productType );
	};

	/**
	 * Prepares the data on single product button click with the necessary information we need etc.
	 * This ensures our functionality works on single product pages too, not just product cards.
	 *
	 * @param {Event}       event
	 * @param {HTMLElement} buttonElement
	 * @param {HTMLElement} formElement
	 * @param {string}      context
	 * @param {string}      productType
	 */
	prepareSubmitData = ( event, buttonElement, formElement, context = 'single-product', productType = 'variable' ) => {
		// Don't do anything if the button is considered disabled by WC
		if ( buttonElement.classList.contains( 'disabled' ) || buttonElement.disabled || buttonElement.dataset?.disabled === 'yes' ) {
			return;
		}

		// Lets see if we've disabled our feature for this specific product.
		const disabledInput = formElement.querySelector( '.qala-buy-disabled' );
		if ( disabledInput && disabledInput.value === 'yes' ) {
			return;
		}

		// First attempt to get the product ID from the button
		let productId = buttonElement.value;
		let variationId = null;
		let variationData = null;
		const quantityElement = ( context === 'single-product' ) ? formElement?.querySelector( 'input[name="quantity"]' ) : this.modal.querySelector( 'input[name="quantity"]' );
		const quantity = quantityElement ? quantityElement.value : 1;

		// If we didn't get product ID from the button, lets get it from the sibling elements.
		// This usually means we are either in the quickview modal or dealing with variable products.
		if ( ! productId ) {
			if ( productType === 'simple' ) {
				// Support for simple products.
				productId = this.modal.querySelector( '.qala-quickview__footer input[name="product_id"]' )?.value;
			} else {
				// Support for variable products.
				productId = formElement.querySelector( 'input[name="product_id"]' )?.value;
				variationData = [];
				// If we are still missing productId lets just bail.
				if ( ! productId ) {
					return;
				}

				variationId = formElement.querySelector( 'input[name="variation_id"]' )?.value;
				let productVariations = formElement.dataset.product_variations;
				// cant do anything if there is no variation data to use.
				if ( ! productVariations ) {
					return;
				}

				productVariations = JSON.parse( productVariations );

				// Locate our variation based off the selected variation ID.
				const foundVariation = productVariations.find( variation => {
					return variation.variation_id === parseInt( variationId );
				} );

				// If we found a variation, lets get the variation attributes and push them to our data array.
				if ( foundVariation && foundVariation?.variation_is_active ) {
					const attributes = Object.entries( foundVariation.attributes );
					attributes.forEach( ( [ key, value ] ) => {
						// WooCommerce wants the key as `pa_{our-key}` rather than `attribute_pa_{our-key}` for some dumb reason.
						const cleanedKey = key.replace( 'attribute_', '' );
						const attributeObject = {
							attribute: cleanedKey,
							value,
						};
						variationData.push( attributeObject );
					} );
				}
			}
		}

		// Allow external scripts to bypass our add to cart by passing true to this filter.
		const bypassAddToCart = wp.hooks.applyFilters( 'qalaBuy.filters.bypassAddToCart', false, buttonElement, formElement, context, productType );
		if ( bypassAddToCart === true ) {
			return;
		}

		// Now we can be sure we want to prevent that link click!
		event.preventDefault();

		// Set loading state on the button trigger.
		this.setButtonLoadState( productId );

		// Finally run the add to cart action.
		this.addToCart( productId, variationData, variationId, quantity, context );
	};

	/**
	 * Trigger the quickview functionality.
	 * Depending on the product type we do different things.
	 *
	 * @param {Event}       event
	 * @param {HTMLElement} trigger
	 */
	triggerQuickview = ( event, trigger ) => {
		let productId = trigger.dataset?.product_id ?? '';
		const productType = trigger.dataset?.product_type ?? '';
		const disabled = trigger.dataset?.disabled ?? 'no';

		// If we have set the functionality to be disabled here we simply bail!
		if ( disabled === 'yes' ) {
			return;
		}

		// Only apply our logic to product types we support.
		if ( ! this.supportedProductTypes.includes( productType ) ) {
			return;
		}

		// If the button that triggered this has the notify class that means we already know the product is out of stock and we should'nt continue.
		if ( trigger.classList.contains( this.notifyClass ) ) {
			return;
		}

		// If we don't have a product ID, lets try to grab it from the variation instead.
		if ( ! productId || productId === '' ) {
			productId = trigger.dataset?.variation_id ?? '';
			// Add it as temporary data to the product element so the rest of our logic works as expected.
			if ( productId ) {
				trigger.dataset.product_id = productId;
			}
		}

		// If we still don't have a product ID lets bail.
		if ( ! productId ) {
			return;
		}

		// Now we can be sure we want to prevent that link click!
		event.preventDefault();

		// If the button is an error we dismiss it and bail.
		if ( trigger.classList.contains( this.errorClass ) ) {
			this.dismissError( productId );
			return;
		}

		// Set loading state on the button trigger.
		this.setButtonLoadState( productId );

		// Handle what comes next depending on the product type and admin settings.
		if ( productType === 'variable' ) {
			this.displayProduct( productId );
		} else if ( productType === 'simple' ) {
			if ( QalaBuy?.settings?.show_simple_product_in_modal === 'yes' ) {
				this.displayProduct( productId );
			} else {
				this.addToCart( productId );
			}
		}
	};

	/**
	 * Dismiss the error shown on a quickview product.
	 *
	 * @param {number} productId
	 */
	dismissError = productId => {
		const buttons = document.querySelector( '[data-product_id="' + productId + '"]' );
		if ( ! buttons ) {
			return;
		}

		buttons.forEach( button => {
			const message = button.closest( '.quickview-product' ).querySelector( '.qala-quickview__error' );
			if ( message ) {
				message.remove();
			}
			button.classList.remove( this.errorClass );
		} );
	};

	/**
	 * Gets all related button elements to this instance.
	 *
	 * If we are passing a product ID we also attempt to grab buttons related to the product in product listings etc.
	 *
	 * @param {number} productId
	 */
	getButtonElements = ( productId = null ) => {
		let buttons = [];
		if ( productId ) {
			buttons = document.querySelectorAll( 'button[data-product_id="' + productId + '"], button[value="' + productId + '"], .single_add_to_cart_button' );
		} else {
			buttons = document.querySelectorAll( '.single_add_to_cart_button' );
		}

		return buttons;
	};

	/**
	 * Set loading state on the button(s) for this product.
	 *
	 * @param {number} productId
	 */
	setButtonLoadState = productId => {
		const buttons = this.getButtonElements( productId );

		buttons.forEach( button => {
			button.classList.add( this.loadingClass );
			button.setAttribute( 'aria-disabled', 'true' );
			button.disabled = true;
		} );
	};

	/**
	 * Reset loading state on the button(s) for this product id
	 *
	 * @param {number} productId
	 */
	resetButtonLoadState = productId => {
		const buttons = this.getButtonElements( productId );

		buttons.forEach( button => {
			button.classList.remove( this.loadingClass );
			button.removeAttribute( 'aria-disabled' );
			button.disabled = false;
		} );
	};

	/**
	 * Set disabled state on the add to cart button for this product.
	 *
	 */
	setButtonDisabledState = () => {
		const buttons = this.getButtonElements();

		buttons.forEach( button => {
			button.classList.add( this.disabledClass );
			button.setAttribute( 'aria-disabled', 'true' );
			button.disabled = true;
		} );
	};

	/**
	 * Set loading state on the Add to cart button for this product.
	 *
	 */
	setButtonEnabledState = () => {
		const buttons = this.getButtonElements();

		buttons.forEach( button => {
			button.classList.remove( this.disabledClass );
			button.removeAttribute( 'aria-disabled' );
			button.disabled = false;
		} );
	};

	/**
	 * This takes the event of a change on an input.
	 * It stores the current value of it.
	 * It then takes all the classnames from that input and finds every element in the modal with the same classnames.
	 * Then it sets the value of all those inputs to the current value.
	 *
	 * Effectively it mirrors multiple inputs values.
	 *
	 * @param {Event} event
	 */
	mirrorInputValue = event => {
		const element = event.target;
		const value = element.value;
		const classTargets = element.className.split( ' ' );
		const syncElements = this.modal.querySelectorAll( '.' + classTargets.join( '.' ) );

		syncElements.forEach( syncElement => {
			syncElement.value = value;
		} );
	};

	/**
	 * Sets up to display the product in Quickview.
	 *
	 * @param {number} productId
	 */
	async displayProduct( productId ) {
		this.toggleQuickview();
		this.toggleModalLoader();
		let productData = await this.api.fetchProduct( productId );

		/**
		 * Filters the product data.
		 *
		 * @param {string} filterName - The name of the filter being applied.
		 * @param {Object} data       - The product data
		 *
		 * @return {Object} - The filtered data.
		 */
		productData = wp.hooks.applyFilters( 'qalaBuy.filter.productData', productData );

		if ( productData instanceof Error ) {
			// If the retrieval of the product is an error we reset everything and output the error.
			this.resetButtonLoadState( productId );
			this.toggleModalLoader();
			this.toggleQuickview();
			this.showError( productId, productData.message, 'card', productData.cause );
			return;
		}

		this.populateQuickview( productData );
		this.resetButtonLoadState( productId );
		this.toggleModalLoader();
	}

	/**
	 * Populates the quickview modal with the product data.
	 *
	 * @param {Object} productData
	 */
	populateQuickview = productData => {
		// Set up the product data.
		const productTemplate = wp.template( 'quickview-product' );
		const body = this.modal.querySelector( '.body__content' );
		body.innerHTML = productTemplate( productData );

		// Add event listener to our form selects, we use this to determine changes to the variation selections.
		const selectElements = body.querySelectorAll( '.variations_form select' );
		selectElements.forEach( selectElement => {
			selectElement.addEventListener( 'change', this.changeVariationSelection );
		} );

		// Set up the add to cart button.
		const addToCartButtonTemplate = wp.template( 'quickview-add-to-cart-button' );
		const addToCartWrapper = this.modal.querySelector( '.qala-quickview__footer .actions__add-to-cart' );
		addToCartWrapper.innerHTML = addToCartButtonTemplate( productData );

		// See if there's a variation form for our product.
		// Only then do we set the disabled state of our button.
		const hasVariationForm = this.modal.querySelector( '.qala-quickview .variations_form' );
		if ( hasVariationForm || productData.stock_status === 'outofstock' ) {
			this.setButtonDisabledState();
		}

		// Add the event listener to our button
		const addToCartButton = addToCartWrapper.querySelector( 'button[type="submit"]' );
		if ( addToCartButton ) {
			addToCartButton.addEventListener( 'click', this.addToCartTriggered );
		}

		// Add listener to the quantity and variation id inputs.
		const syncedInputs = this.modal.querySelectorAll( 'input.qty, input.variation_id' );
		syncedInputs.forEach( input => {
			input.addEventListener( 'change', this.mirrorInputValue );
			// Since WC relies on jQuery we have to also add a jQuery change listener and simulate the standard
			jQuery( input ).on( 'change', event => this.mirrorInputValue( event ) );
		} );

		// Lets set up the variations JS from WC core on our form.
		// This handles variation selection logic for us.
		// eslint-disable-next-line camelcase
		if ( typeof wc_add_to_cart_variation_params !== 'undefined' ) {
			jQuery( '.qala-quickview .variations_form' ).each( function() {
				jQuery( this ).wc_variation_form();
			} );
		}

		/**
		 * Action happening after the quickview has been populated.
		 *
		 * This hooks can be used to run things after the quickview have been populated.
		 *
		 * @param {string} filterName  - The name of the filter being applied.
		 * @param {Object} productData - The productData that you can use.
		 * @param {Object} this        - The current JS class instance
		 */
		wp.hooks.doAction( 'qalaBuy.action.after_quickview_populated', productData, this );

		this.setupModalVariationState( productData );
	};

	/**
	 * Setup modal-scoped variation state and mirror Woo 9+/10+ events.
	 *
	 * - Uses the JSON on data-product_variations via Woo’s own events.
	 * - Keeps the quickview submit button in sync (because our button lives outside the form).
	 *
	 * @param {Object} productData - The product data rendered in the modal.
	 */
	setupModalVariationState = productData => {
		// Locate the quickview modal and its variation form.
		const modalRootElement = document.getElementById( this.modalClass );
		if ( ! modalRootElement ) {
			return;
		}
		const variationFormElement = modalRootElement.querySelector( '.variations_form' );

		// Determine initial state (e.g., default variation preselected).
		const hasVariationFormInModal = Boolean( variationFormElement );
		const variationIdInputElement = hasVariationFormInModal
			? variationFormElement.querySelector( 'input.variation_id' )
			: null;
		const variationIsChosenInitially = Boolean( variationIdInputElement && variationIdInputElement.value );

		// Preload guard:
		// - Disable when product is out of stock.
		// - Disable when variable product has no chosen variation yet.
		// - Otherwise enable now (covers simple products and defaulted variations).
		if ( productData.stock_status === 'outofstock' ) {
			this.setEnabledStateForModal( false );
		} else if ( hasVariationFormInModal && ! variationIsChosenInitially ) {
			this.setEnabledStateForModal( false );
		} else {
			this.setEnabledStateForModal( true );
		}

		// If there is no variation form, or Woo’s variation script is not present, we are done.
		// eslint-disable-next-line camelcase
		if ( ! hasVariationFormInModal || typeof wc_add_to_cart_variation_params === 'undefined' ) {
			return;
		}

		const $variationForm = jQuery( variationFormElement );

		// When Woo finds a variation, enable only if purchasable and available (stock/backorders).
		$variationForm.on( 'found_variation', ( jqueryEventObject, variationObject ) => {
			const variationExists = Boolean( variationObject );
			const variationIsAvailable =
				Boolean( variationObject?.is_in_stock ) ||
				Boolean( variationObject?.in_stock ) ||
				Boolean( variationObject?.backorders_allowed );
			const variationIsPurchasable = variationObject?.is_purchasable !== false;

			const shouldEnable = variationExists && variationIsAvailable && variationIsPurchasable;
			this.setEnabledStateForModal( shouldEnable );
		} );

		// When Woo resets or hides the variation, disable again.
		$variationForm.on( 'reset_data hide_variation', () => {
			this.setEnabledStateForModal( false );
		} );

		// Force an initial evaluation so default selections (if any) apply immediately.
		$variationForm.trigger( 'check_variations' );
	};
	/**
	 * Enable/disable ONLY the quickview modal controls.
	 *
	 * This helper intentionally targets the modal's submit button
	 * and enables/disables it based on the passed boolean
	 *
	 * @param {boolean} shouldEnable
	 */
	setEnabledStateForModal = shouldEnable => {
		const modalRootElement = document.getElementById( this.modalClass );
		if ( ! modalRootElement ) {
			return;
		}

		const submitButtonElement = modalRootElement.querySelector( '.' + this.pdpTarget );
		if ( ! submitButtonElement ) {
			return;
		}
		const isDisabled = ! shouldEnable;

		if ( submitButtonElement ) {
			submitButtonElement.classList.toggle( this.disabledClass, isDisabled );
			submitButtonElement.disabled = isDisabled;

			// Remove aria-disabled entirely when enabled to avoid [aria-disabled] CSS matches
			if ( isDisabled ) {
				submitButtonElement.setAttribute( 'aria-disabled', 'true' );
			} else {
				submitButtonElement.removeAttribute( 'aria-disabled' );
			}
		}
	};
	/**
	 * Toggle Quickview modal.
	 */
	toggleQuickview = () => {
		this.wrapper.classList.toggle( 'is-visible' );
		this.overlay.classList.toggle( 'is-visible' );
		this.body.classList.toggle( 'qala-buy-open' );
	};

	/**
	 * Toggle Quickview modal loader.
	 */
	toggleModalLoader = () => {
		this.modalLoader.classList.toggle( 'is-loaded' );
	};

	/**
	 * Add the product to the cart.
	 *
	 * @param {number} productId     the product id (parent id if variation)
	 * @param {Array}  variationData variation data.
	 * @param {number} variationId   Variation product ID.
	 * @param {number} quantity      Quantity to add to cart.
	 * @param {string} origin        determines the context the call originated from.
	 */
	async addToCart( productId, variationData = null, variationId = null, quantity = 1, origin = 'card' ) {
		this.removeErrorNotices();
		const response = await this.api.addToCart( productId, variationData, quantity );
		if ( response instanceof Error ) {
			// If we failed due to invalid nonce lets generate a new one and try again.
			// this can happen for example if a user comes back to a stale tab or if they log in and don't refresh the tab.
			if ( response.cause === 'woocommerce_rest_invalid_nonce' ) {
				await this.api.generateNonce();
				this.addToCart( productId, variationData, variationId, quantity, origin );
			}

			this.resetButtonLoadState( productId );
			this.showError( productId, response.message, origin, response.cause );
			return;
		}
		if ( response ) {
			const cartContent = response;
			this.resetButtonLoadState( productId );
			this.notifyExternals( productId, variationData, variationId, cartContent );

			// Close quickview on success.
			if ( origin === 'quickview' ) {
				this.toggleQuickview();
			}
		}
	}

	/**
	 * Create an error message for the product.
	 *
	 * @param {number} productId - the product ID.
	 * @param {string} message   - the error message from the response.
	 * @param {string} type      - the type of error we are dealing with.
	 * @param {string} cause     - the error code from the response.
	 */
	showError = ( productId, message, type = 'card', cause = '' ) => {
		switch ( type ) {
			case 'single-product':
				this.showErrorPdp( productId, message );
				break;
			case 'quickview':
				this.showErrorQuickview( productId, message );
				break;
			case 'card':
			default:
				this.showErrorCard( productId, message, cause );
		}
	};

	/**
	 * Show error on a product card.
	 *
	 * @param {number} productId
	 * @param {string} message
	 * @param {string} cause
	 */
	showErrorCard = ( productId, message, cause = '' ) => {
		// there might be more than one instance of this product so we might as well show the error on all.
		const buttons = document.querySelectorAll( '[data-product_id="' + productId + '"]' );
		if ( ! buttons ) {
			return;
		}
		buttons.forEach( button => {
			const product = button.closest( '.quickview-product' );
			const errorSpan = document.createElement( 'span' );
			errorSpan.classList.add( 'qala-quickview__error' );
			errorSpan.innerHTML = message;
			product.insertBefore( errorSpan, button );
			// If we have the notify functionality button and the cause of error is out of stock we want to show a more contextual button.
			const hasNotify = button.querySelector( '.button__notify' );
			if ( cause === 'woocommerce_rest_product_out_of_stock' && hasNotify ) {
				button.classList.add( 'quickview-product__button--notify' );
				button.classList.remove( 'quickview-product__button--error' );
				const productLink = button.closest( 'a' );
				if ( productLink ) {
					const productLinkURL = new URL( productLink.href ); // Create a new URL object
					productLinkURL.hash = 'wc_bis_product_form'; // Append #wc_bis_product_form to the URL's hash property
					productLink.href = productLinkURL.href; // Update the button's href attribute with the updated URL
				}
			} else {
				button.classList.remove( 'quickview-product__button--notify' );
				button.classList.add( 'quickview-product__button--error' );
			}

			button.removeAttribute( 'aria-disabled' );
			button.removeAttribute( 'disabled' );
		} );
	};

	/**
	 * Show error on a product PDP.
	 *
	 * @param {number} productId
	 * @param {string} message
	 */
	showErrorPdp = ( productId, message ) => {
		this.removeErrorNotices();
		// Set up the notice template
		const noticeTemplate = wp.template( 'quickview-error-notice' );
		const form = this.body.querySelector( 'form.cart' );
		const errorDiv = document.createElement( 'div' );
		errorDiv.classList.add( 'woocommerce-notices-wrapper', 'qala-quickview__error-wrapper' );
		errorDiv.dataset.product_id = productId;
		errorDiv.innerHTML = noticeTemplate( { message } );
		// add the message after the form.
		form.after( errorDiv );
		// Set up eventlistener to close the error div.
		this.setUpErrorCloseButton( errorDiv );
	};

	/**
	 * Show error on a product PDP.
	 *
	 * @param {number} productId
	 * @param {string} message
	 */
	showErrorQuickview = ( productId, message ) => {
		this.removeErrorNotices();
		// Set up the notice template
		const noticeTemplate = wp.template( 'quickview-error-notice' );
		const containerElement = this.modal.querySelector( '.qala-quickview__footer' );
		const errorDiv = document.createElement( 'div' );
		errorDiv.classList.add( 'woocommerce-notices-wrapper', 'qala-quickview__error-wrapper' );
		errorDiv.dataset.product_id = productId;
		errorDiv.innerHTML = noticeTemplate( { message } );
		// add the message before the form.
		containerElement.before( errorDiv );
		// Set up eventlistener to close the error div.
		this.setUpErrorCloseButton( errorDiv );
	};

	/**
	 * Sets up the closing button of the newly generated error div.
	 *
	 * @param {HTMLElement} parentElement - The parent error div.
	 */
	setUpErrorCloseButton( parentElement ) {
		const dismissButton = parentElement.querySelector( '.dismiss-error' );
		if ( ! dismissButton ) {
			return;
		}
		dismissButton.addEventListener( 'click', event => {
			event.preventDefault();
			parentElement.remove();
		} );
	}

	/**
	 * Remove any existing error notices on the page.
	 */
	removeErrorNotices = () => {
		// remove any existing error.
		const errors = this.body.querySelector( '.qala-quickview__error-wrapper' );
		if ( errors ) {
			errors.remove();
		}
	};

	/**
	 * Remove previous notices if an element in the cart form changes.
	 *
	 * @param {Event} event
	 */
	cartFormChangeListener = event => { // eslint-disable-line no-unused-vars
		if ( event.target.tagName === 'SELECT' || event.target.tagName === 'INPUT' ) {
			this.removeErrorNotices();
		}
	};

	/**
	 * Notify external sources that the cart have changed!
	 *
	 * Dispatch our Qala Minicarts custom event to tell it we have added a product to the cart via our Fetch API.
	 *
	 * @param {number} productId
	 * @param {Object} variationData
	 * @param {number} variationId
	 * @param {Object} cartContent
	 */
	notifyExternals = ( productId, variationData = {}, variationId = null, cartContent = {} ) => {
		// Before we notify cart, make sure there isn't any buttons still loading.
		const loadingButtons = document.querySelector( '.' + this.loadingClass );
		if ( loadingButtons ) {
			return;
		}

		// Notify the native WC functionality that the cart should be updated.
		if ( window.jQuery ) {
			jQuery( document.body ).trigger( 'update_checkout' );
		}

		// Dispatch the Qala minicarts custom event.
		// We pass the full cart response here as well which means the minicart doesnt have to fetch it again!
		const eventData = {
			detail: {
				product_id: productId ?? 0,
				forceUpdate: false,
				cart: cartContent,
				variation: variationData,
				variationId,
			},
		};
		window.dispatchEvent( new CustomEvent( 'product-added-to-cart', eventData ) );
	};

	/**
	 * Handles clicks outside of the modal.
	 *
	 * @param {Event} event
	 */
	outsideClickListener = event => {
		if ( ! event.target.classList.contains( this.modalClass + '__overlay' ) ) {
			return;
		}
		this.toggleQuickview();
	};

	/**
	 * Closes the quickview modal on close button click.
	 */
	modalCloseClickListener = () => {
		this.toggleQuickview();
	};
}

export default Quickview;
